import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createOrder } from '../../entities/order/model';
import { LoadingWrapper } from '../../shared/ui/loading-wrapper';
import MainLayout from '@shared/ui/main-layout';
import { CartConfirmStepBlock } from '@shared/ui/cart-confirm-step-block';
import {
  CartStepContactDetails,
  CartStepDeliveryMethods,
  CartStepYourCart,
} from '../../widgets/cart-confirm';
import { formatAddress } from '@shared/lib/format-address';
import {
  deliveryTypes,
  payerTypes,
  recipientTypes,
  selfDeliveryTypes,
} from '@shared/config/delivery-options';
import { yupResolver } from '@hookform/resolvers/yup';
import { cartConfirmSchema } from '../../widgets/cart-confirm/lib/cart-confirm-schema';
import { useForm } from 'react-hook-form';
import { CartAddAddressModal } from '../../widgets/cart-confirm';
import { EmptyState } from '../../shared/ui/empty-state';
import {
  selectCartInitialLoading,
  selectCartWithCalculatedPrices,
  getCart,
} from '../../entities/cart/model';
import { AppDispatch } from '../../app/store/store';
import {
  Address,
  DeliveryTypeEnum,
  ICreateOrder,
} from '../../types/interfaces';
import { selectOrdersStatus } from '../../entities/order/model';
import { cartConfirmSteps } from './utils/constants';
import { useMe } from '../Area/Area';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { useIsKzCompany } from '@shared/lib/use-is-kz';

interface IContactFromStorage {
  contact_full_name: string;
  contact_phone: string;
  contact_email: string;
  courier_service?: string;
}

type CartConfirmSchema = yup.InferType<typeof cartConfirmSchema>;

function Cart() {
  const cartItems = useSelector(selectCartWithCalculatedPrices);
  // НЕ берём `selectCartStatus` — он `loading` при любых мутациях
  // корзины (изменение количества, удаление товара), и LoadingWrapper
  // ниже скрывал бы всю страницу на каждое нажатие «+/-». Используем
  // селектор «initial load only»: возвращает `loading` лишь когда
  // корзина впервые открывается с пустым списком и идёт первый GET.
  const status = useSelector(selectCartInitialLoading);
  const isKz = useIsKzCompany();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const contactStorage = localStorage.getItem('contact');
  const addressLocal = localStorage.getItem('addresses');
  const [addressFromStorage, setAddressFromStorage] = useState<
    { label: any; value: any; fullAddress: Address }[]
  >(addressLocal ? JSON.parse(addressLocal) : []);
  const [cartSteps, setCartSteps] = useState('0');

  const contactFromStorage: IContactFromStorage = contactStorage
    ? JSON.parse(contactStorage)
    : {
        contact_full_name: '',
        contact_phone: '',
        contact_email: '',
        courier_service: '',
      };

  const total = cartItems.reduce(
    (sum, item) => sum + (item.totalPrice || 0),
    0
  );
  const totalQuantityThatCounts = cartItems.reduce(
    (sum, i) => (i.do_not_count ? sum : sum + i.quantity),
    0
  );

  const { me } = useMe();
  const {
    register: cartRegister,
    handleSubmit: cartHandleSubmit,
    setValue,
    watch,
    control,
    trigger,
    resetField,
    formState: { errors: cartErrors, isValid },
  } = useForm<CartConfirmSchema>({
    mode: 'onChange',
    resolver: yupResolver(cartConfirmSchema),
    defaultValues: {
      ...contactFromStorage,
      deliveryEnabled: false,
      comment: ' ',
      deliveryMode: 'delivery',
      payer_type: totalQuantityThatCounts >= 10 ? payerTypes[1] : payerTypes[0],
      delivery_type: deliveryTypes[0],
      recipient_type: recipientTypes[0],
      self_delivery_type: selfDeliveryTypes[0],
      add_pickup_details_later: true,
      third_party_company_inn: '',
      third_party_company_name: '',
      legal_entity: {
        label: me?.company?.legal_entities?.[0]?.name,
        value: me?.company?.legal_entities?.[0]?.id.toString(),
      },
      shipmentAddress: {
        label: formatAddress(
          me?.company?.legal_entities?.[0]?.addresses?.[0] ||
            addressFromStorage[0]?.fullAddress
        ),
        value:
          me?.company?.legal_entities?.[0]?.addresses?.[0]?.id ||
          addressFromStorage[0]?.value,
        fullAddress:
          me?.company?.legal_entities?.[0]?.addresses?.[0] ||
          addressFromStorage[0]?.fullAddress,
      },
    },
  });

  const recipientType = watch('recipient_type');
  const payerType = watch('payer_type');
  const deliveryType = watch('delivery_type');
  const deliveryMode = watch('deliveryMode') || '';
  const shipmentAddress = watch('shipmentAddress');
  const createOrderStatus = useSelector(selectOrdersStatus);
  const selfDeliveryType = watch('self_delivery_type') || {
    value: null,
    label: null,
  };

  useEffect(() => {
    dispatch(getCart());
  }, [dispatch]);

  useEffect(() => {
    setValue(
      'payer_type',
      totalQuantityThatCounts >= 10 ? payerTypes[1] : payerTypes[0]
    );
  }, [totalQuantityThatCounts, setValue]);

  const handleAddressSubmit = (addr: Address) => {
    const exists = addressFromStorage.find(
      (a: any) => a.fullAddress.id === addr.id
    );
    const newAddr = exists
      ? exists
      : {
          label: formatAddress(addr),
          value: formatAddress(addr),
          fullAddress: addr,
        };
    setValue('shipmentAddress', newAddr);
    const updatedAddresses = [
      ...addressFromStorage.filter((a: any) => a.value !== newAddr.value),
      newAddr,
    ].slice(0, 5);
    setAddressFromStorage(updatedAddresses);
    localStorage.setItem('addresses', JSON.stringify(updatedAddresses));
  };

  function onSubmit(data: CartConfirmSchema) {
    const address = data?.shipmentAddress?.fullAddress || {};
    const constantValues: IContactFromStorage = {
      contact_full_name: data.contact_full_name,
      contact_phone: data.contact_phone,
      contact_email: data.contact_email,
    };

    const formattedItems = cartItems
      .flatMap((item) => {
        const mainItem = {
          quantity: item.quantity,
          price: item?.price?.toString() || '0',
          item: item.id,
          item_name: item.name,
          cancel: false,
        };

        const items = [mainItem];

        if (item.verification_item && item.verification_quantity > 0) {
          const verificationItem = {
            quantity: item.verification_quantity,
            price: item?.verificationPrice?.toString() || '0',
            item: item.verification_item.id,
            item_name: item.verification_item.name,
            cancel: false,
          };
          items.push(verificationItem);
        }

        return items;
      })
      .filter((item) => item.quantity > 0);

    localStorage.setItem(
      'contact',
      JSON.stringify({
        ...constantValues,
        courier_service: data.courier_service,
      })
    );

    const cartData = {
      comment: data.comment,
      company: me?.company?.id || 0,
      legal_entity: data.legal_entity.value,
      items: formattedItems,
      ...constantValues,
    };

    if (!data.deliveryEnabled) {
      const orderData: ICreateOrder = {
        delivery_type: DeliveryTypeEnum.Unspecified,
        ...cartData,
        ...address,
      };
      dispatch(createOrder(orderData)).then(() => {
        navigate('/orders');
      });
      return;
    }

    if (deliveryMode === 'self-delivery') {
      if (selfDeliveryType.value === 'courier') {
        const orderData: ICreateOrder = {
          ...cartData,
          delivery_type: 'self-delivery',
          self_delivery_type: 'courier',
          courier_service: data.courier_service,
          invoice_url: data.invoice_url,
        };
        dispatch(createOrder(orderData)).then(() => {
          navigate('/orders');
        });
        return;
      }
      if (selfDeliveryType.value === 'confidant') {
        const orderData: ICreateOrder = {
          ...cartData,
          self_delivery_type: 'confidant',
          delivery_type: 'self-delivery',
          confidant_name: data.confidant_name,
          confidant_number: data.confidant_number,
        };
        dispatch(createOrder(orderData)).then(() => {
          navigate('/orders');
        });
        return;
      }
    }

    if (deliveryMode === 'delivery') {
      let reqData: ICreateOrder = {
        ...cartData,
        ...address,
        delivery_type: 'other',
        payer_type: data.payer_type.value,
        recipient_type: data.recipient_type.value,
      };

      // Для KZ выбор ТК скрыт (см. CartStepDeliveryMethods), в форме
      // лежит дефолтная российская ТК — её отправлять нельзя, пусть
      // будет 'unspecified', доставку согласует менеджер.
      if (payerType?.value === 'recipient' && !isKz) {
        reqData.delivery_type = data.delivery_type.value;
      } else if (isKz) {
        reqData.delivery_type = DeliveryTypeEnum.Unspecified;
      }

      if (recipientType?.value === 'third party') {
        reqData.third_party_company_inn = data.third_party_company_inn;
        reqData.third_party_company_name = data.third_party_company_name;
      }

      dispatch(createOrder(reqData)).then(() => {
        navigate('/orders');
      });
    }
  }

  const setStepOnDelivery = () => {
    setCartSteps('2');
    setValue('deliveryEnabled', true);
  };
  const setYourCartStep = () => {
    setCartSteps('0');
    setValue('deliveryEnabled', false);
  };
  const setStepOnContacts = () => {
    setCartSteps('1');
    setValue('deliveryEnabled', false);
  };

  // Привязываем список доступных адресов к ВЫБРАННОМУ юр.лицу,
  // а не к первому из массива. Раньше тут было legal_entities?.[0],
  // и при смене юрлица в шаге «Контакты» юзер всё равно видел адреса
  // первого LE — это бесило, особенно у компаний с несколькими юрлицами.
  const selectedLegalEntityIdRaw = watch('legal_entity')?.value;
  const selectedLegalEntity = useMemo(() => {
    const entities = me?.company?.legal_entities ?? [];
    if (!entities.length) return undefined;
    if (selectedLegalEntityIdRaw == null) return entities[0];
    // value в форме приходит то строкой, то числом (default — toString,
    // CartSelect — числом), сравниваем через String() с обоих концов.
    return (
      entities.find(
        (le) => String(le.id) === String(selectedLegalEntityIdRaw)
      ) ?? entities[0]
    );
  }, [selectedLegalEntityIdRaw, me?.company?.legal_entities]);

  // Если пользователь сменил юр.лицо, а в форме всё ещё лежит адрес
  // прошлого LE — текущий выбор уже не валиден. Сбрасываем на первый
  // адрес выбранного LE (или на null, если у того адресов нет вовсе).
  useEffect(() => {
    if (!selectedLegalEntity) return;
    const currentAddr = shipmentAddress?.fullAddress;
    if (!currentAddr) return;
    const belongsToLE = (selectedLegalEntity.addresses ?? []).some(
      (a) => a.id === currentAddr.id
    );
    if (belongsToLE) return;
    const first = selectedLegalEntity.addresses?.[0];
    if (first) {
      setValue('shipmentAddress', {
        label: formatAddress(first),
        value: first.id,
        fullAddress: first,
      } as any);
    } else {
      setValue('shipmentAddress', null as any);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLegalEntity?.id]);

  const addresses = [
    {
      label: selectedLegalEntity?.name
        ? `Адреса юр. лица «${selectedLegalEntity.name}»`
        : 'Юридического лица',
      options: (selectedLegalEntity?.addresses ?? []).map((a) => ({
        label: formatAddress(a),
        value: formatAddress(a),
        fullAddress: a,
      })),
    },
    {
      label: 'Недавние',
      options: addressFromStorage.map((a) => ({
        label: formatAddress(a.fullAddress),
        value: formatAddress(a.fullAddress),
        fullAddress: a,
      })),
    },
  ];
  return (
    <LoadingWrapper type="main" status={status}>
      <MainLayout>
        <MainLayout.Title>Оформление заказа</MainLayout.Title>
        <MainLayout.Header isVisible={cartItems.length > 0}>
          <div className="flex items-center gap-5 w-full" id="cart-steps">
            {Object.keys(cartConfirmSteps).map((key) => (
              <CartConfirmStepBlock
                key={key}
                title={cartConfirmSteps[key].title}
                active={key === cartSteps}
              />
            ))}
          </div>
        </MainLayout.Header>
        <MainLayout.Main>
          {cartItems.length ? (
            <>
              {cartSteps === '0' && (
                <CartStepYourCart
                  cartItems={cartItems}
                  total={total}
                  setCartSteps={setCartSteps}
                />
              )}
              {cartSteps !== '0' && (
                <div className="min-h-[588px] bg-white rounded-2xl p-6 mt-6 flex flex-col justify-between">
                  <form
                    className="flex flex-col justify-between flex-1"
                    onSubmit={cartHandleSubmit(onSubmit)}
                  >
                    {(() => {
                      switch (cartSteps) {
                        case '1':
                          return (
                            <CartStepContactDetails
                              setValue={setValue}
                              control={control}
                              watch={watch}
                              cartErrors={cartErrors}
                              register={cartRegister}
                              setStepOnDelivery={setStepOnDelivery}
                              setYourCartStep={setYourCartStep}
                              isValid={isValid}
                              status={createOrderStatus}
                              reset={resetField}
                              trigger={trigger}
                            />
                          );
                        case '2':
                          return (
                            <CartStepDeliveryMethods
                              setStepOnContacts={setStepOnContacts}
                              cartRegister={cartRegister}
                              cartErrors={cartErrors}
                              shipmentAddress={shipmentAddress}
                              deliveryType={deliveryType}
                              payerType={payerType}
                              setValue={setValue}
                              recipientType={recipientType}
                              deliveryMode={deliveryMode}
                              watch={watch}
                              status={createOrderStatus}
                              address={addresses}
                              selfDeliveryType={selfDeliveryType}
                              trigger={trigger}
                              itemsLength={totalQuantityThatCounts}
                            />
                          );
                        default:
                          break;
                      }
                    })()}
                  </form>
                  {deliveryMode !== 'self-delivery' && cartSteps === '2' && (
                    <div className="flex-1">
                      <CartAddAddressModal
                        setValue={setValue}
                        handleAddressSubmit={handleAddressSubmit}
                      />
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <EmptyState
              title={'Корзина пуста'}
              message={'Добавьте в корзину что-нибудь'}
            />
          )}
        </MainLayout.Main>
      </MainLayout>
    </LoadingWrapper>
  );
}

export default Cart;
